import { Box, CircularProgress, Typography } from "@mui/material";
import SalesItemListPdfTable from "../../../components/Table/Pdf/SalesItemList";
import {
  SalesOrderFindUniqueQuery,
  useSalesOrderFindUniqueQuery,
} from "../../../generated/sales";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import { useNavigate, useParams } from "react-router-dom";
import { ISalesItemList } from "../../../types/Sales";
// import { useSalesItemListFormatter } from "../../../hooks/Sales/use-item-list-formatter";
import SalesPdfLayout from "../../../components/UI/SalesPdf/SalesPdfLayout";
import { IPdfInfo } from "../../../types/global";
import { formatDate } from "../../../utils/Date";
import { renderAddressString } from "../../../utils/Formatter/Global";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import { usePDFMultiplePage } from "../../../hooks/use-pdf-multiple-page";
import { useReactToPrint } from "react-to-print";
import { ReactInstance, useEffect, useRef } from "react";
import { IContactPerson } from "../../../types/Contact/contact";
import {
  ApprovalTemplateFindByDocumentTypeQuery,
  useApprovalTemplateFindByDocumentTypeQuery,
} from "../../../generated/general";
import { useStateContext } from "../../../contexts/auth-context";

type Props = {
  name: string;
};

const SalesOrderPdf = ({ name }: Props) => {
  const { id } = useParams();
  const componentRef = useRef<ReactInstance | null>(null);
  const graphQLClient = createGraphQLClientWithMiddleware("sales");
  const graphQLClientGeneral = createGraphQLClientWithMiddleware("general");
  const { data, isLoading, isSuccess } =
    useSalesOrderFindUniqueQuery<SalesOrderFindUniqueQuery>(graphQLClient, {
      uniqueInput: {
        id: id ? parseInt(id) : undefined,
      },
    });

  const navigate = useNavigate();

  const {
    state: { authUser, permissions },
  } = useStateContext();

  useEffect(() => {
    if (isSuccess && permissions?.sales_order?.view_self) {
      const createdBy = data?.SalesOrderFindUnique?.created_by;
      const relatedUserList = data?.SalesOrderFindUnique?.related_user_list;
      if (
        createdBy?.user_unique_id === authUser?.unique_id ||
        relatedUserList?.find(
          (user) => user.user_unique_id === authUser?.unique_id
        )
      ) {
        return;
      } else {
        navigate(`/unauthorized`, {
          replace: true,
        });
      }
    }
  }, [
    authUser?.unique_id,
    data?.SalesOrderFindUnique?.created_by,
    data?.SalesOrderFindUnique?.related_user_list,
    isSuccess,
    navigate,
    permissions?.sales_order?.view_self,
  ]);

  const { data: approvalTemplates, isLoading: isApprovalLoading } =
    useApprovalTemplateFindByDocumentTypeQuery<ApprovalTemplateFindByDocumentTypeQuery>(
      graphQLClientGeneral,
      {
        documentType: "sales_order",
      }
    );

  const { paginatedItems, startNumbers } = usePDFMultiplePage(
    data?.SalesOrderFindUnique, //data
    "sales_order", // document type
    141, // name and description row width
    700, // max height without footer
    500, // max height with footer
    226.484, // left header minimum height
    226.484, // right header minimum height
    undefined, // left footer minimum height (you don't need to specify this if the document doesn't have remark and tags)
    undefined // right footer minimum height (you don't need to specify this if the document doesn't have summary)
  );

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: data?.SalesOrderFindUnique.unique_id,
  });

  const highlightSection: IPdfInfo = {
    headers: ["เลขที่เอกสาร", "วันที่ออกเอกสาร", "ใช้ได้ถึง"],
    info: [
      data?.SalesOrderFindUnique.unique_id || "-",
      data?.SalesOrderFindUnique.issue_date
        ? formatDate(data?.SalesOrderFindUnique.issue_date)
        : "-",
      data?.SalesOrderFindUnique.due_date
        ? formatDate(data?.SalesOrderFindUnique.due_date)
        : "-",
    ],
  };

  const identityNoBranch = data?.SalesOrderFindUnique.customer_details
    .identity_no
    ? // &&
      // data?.SalesOrderFindUnique.branch
      // ? `${data?.SalesOrderFindUnique.customer_details.identity_no} (${data?.SalesOrderFindUnique.branch.unique_id})`
      `${data?.SalesOrderFindUnique.customer_details.identity_no}`
    : "-";

  const leftSection: IPdfInfo = {
    headers: [
      "ลูกค้า",
      "ที่อยู่",
      "เลขประจำตัวผู้เสียภาษี",
      "เบอร์โทรศัพท์@โทรสาร",
      "อีเมล",
    ],
    info: [
      `${data?.SalesOrderFindUnique.customer_unique_id} ${data?.SalesOrderFindUnique.customer_details.name}`,
      renderAddressString(
        data?.SalesOrderFindUnique.customer_details.billing_address
      ),
      identityNoBranch,
      `${data?.SalesOrderFindUnique.customer_details.phone || "-"}@${
        data?.SalesOrderFindUnique.customer_details.fax || "-"
      }`,
      data?.SalesOrderFindUnique.customer_details.email || "-",
    ],
  };

  const salesPersonFirstName =
    (data?.SalesOrderFindUnique.related_user_list &&
      data?.SalesOrderFindUnique.related_user_list.length > 0 &&
      data.SalesOrderFindUnique.related_user_list[0].first_name) ||
    "-";

  const salesPersonLastname =
    (data?.SalesOrderFindUnique.related_user_list &&
      data?.SalesOrderFindUnique.related_user_list.length > 0 &&
      data.SalesOrderFindUnique.related_user_list[0].last_name) ||
    "";
  const salePerson = salesPersonFirstName + " " + salesPersonLastname;

  const contactPerson: IContactPerson =
    data?.SalesOrderFindUnique.contact_person_list &&
    data?.SalesOrderFindUnique.contact_person_list.length > 0 &&
    data?.SalesOrderFindUnique.contact_person_list[0];

  const contactPersonName = contactPerson
    ? `${contactPerson.title_name}${contactPerson.first_name} ${contactPerson.last_name}`
    : "-";

  const contactPersonPhone = contactPerson
    ? contactPerson.contact_channel_list.find(
        (channel) => channel.contact_channel_type === "phone"
      )?.contact_channel_info || "-"
    : "-";

  const rightSection: IPdfInfo = {
    headers: [
      "อ้างอิงถึง",
      "พนักงานขาย",
      "เครดิต (วัน)",
      "หมายเลขอ้างอิง",
      "ผู้ติดต่อ",
      "เบอร์โทรศัพท์",
    ],
    info: [
      data?.SalesOrderFindUnique.reference_document_list
        ?.map((doc) => doc.document_unique_id)
        .join(", ") || "-",
      salePerson || "-",
      `${data?.SalesOrderFindUnique.credit_day?.toString()} วัน` || "-",
      data?.SalesOrderFindUnique.reference_no || "-",
      contactPersonName,
      contactPersonPhone,
    ],
  };

  if (id && (isLoading || isApprovalLoading)) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const allPages = paginatedItems.length;

  return (
    <Box maxWidth={1650}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5">{name}</Typography>
          <Typography>{`เลขที่เอกสาร ${data?.SalesOrderFindUnique.unique_id}`}</Typography>
        </Box>
        <Box>
          <CustomizedButton
            fullWidth
            title="พิมพ์เอกสาร"
            variant="outlined"
            onClick={handlePrint}
          />
        </Box>
      </Box>
      <Box display={"flex"} justifyContent={"center"}>
        <Box ref={componentRef} sx={{ m: "auto" }}>
          {paginatedItems.map((item, index) => (
            <SalesPdfLayout
              highlightSection={highlightSection}
              leftSection={leftSection}
              rightSection={rightSection}
              name={"ใบสั่งขาย"}
              multiplePage={paginatedItems.length > 1}
              page={index + 1}
              allPages={allPages}
              data={data?.SalesOrderFindUnique}
              summary={index === paginatedItems.length - 1}
              salePerson={salePerson}
              totalAmount={
                data?.SalesOrderFindUnique.net_amount
                  ? data?.SalesOrderFindUnique.net_amount
                  : 0
              }
              documentType="sales_order"
              hideAdditionalDiscount={false}
              showPaymentSection={false} //Change to true to show payment section below footer
              approvalTemplates={
                approvalTemplates?.ApprovalTemplateFindByDocumentType || []
              }
            >
              <SalesItemListPdfTable
                itemList={(item as ISalesItemList[]) || []}
                startNumber={startNumbers[index]}
                isLastPage={index === paginatedItems.length - 1}
                lastTable={
                  paginatedItems[paginatedItems.length - 1].length === 0
                    ? index === paginatedItems.length - 2
                    : index === paginatedItems.length - 1
                }
                totalAmount={
                  data?.SalesOrderFindUnique.net_amount
                    ? data?.SalesOrderFindUnique.net_amount
                    : 0
                }
                hasItems={item.length > 0}
              />
            </SalesPdfLayout>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SalesOrderPdf;
